/**
 * WorkspacePanel - Middle panel (flex: 1)
 *
 * Scrollable card-based canvas architecture:
 *  ┌─────────────────────────────────┐
 *  │ Sticky title bar (metadata)     │
 *  ├─────────────────────────────────┤
 *  │                                 │ ← scrollable
 *  │  ┌─ SQL card (collapsed) ────┐  │
 *  │  │ SELECT w.state, SUM(...   │  │
 *  │  └──────────────────────────┘  │
 *  │                                 │
 *  │  ┌─ Data table card ─────────┐  │
 *  │  │  DataGrid (auto-height)   │  │
 *  │  └──────────────────────────┘  │
 *  │                                 │ ← future: chart card, insight card
 *  └─────────────────────────────────┘
 *
 * Scales to multiple SQL, table, and chart blocks.
 */

import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  Tooltip,
  useTheme,
  Divider,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemText,
  Skeleton,
  CircularProgress,
  Collapse,
} from '@mui/material';
import { AppDialog } from '../../../components/dialog';
import VoiceCallApprovalCard from './VoiceCallApprovalCard';
import type { VoiceCallApproval } from '../../../types/voiceCall';
import { SmartTable } from '../../../components/table';
import { SnackbarContext } from '../../../ui/SnackbarProvider';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import CodeRounded from '@mui/icons-material/CodeRounded';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import SpeedRounded from '@mui/icons-material/SpeedRounded';
import UnfoldMoreRounded from '@mui/icons-material/UnfoldMoreRounded';
import UnfoldLessRounded from '@mui/icons-material/UnfoldLessRounded';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import CancelOutlined from '@mui/icons-material/CancelOutlined';
import GppGoodRounded from '@mui/icons-material/GppGoodRounded';
import DashboardCustomizeRounded from '@mui/icons-material/DashboardCustomizeRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import ArrowDropDownRounded from '@mui/icons-material/ArrowDropDownRounded';
import OpenInFullRounded from '@mui/icons-material/OpenInFullRounded';
import LinkRounded from '@mui/icons-material/LinkRounded';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import AccessTimeRounded from '@mui/icons-material/AccessTimeRounded';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded';
import { formatRelativeTime, formatAbsolute } from '../../../utils/date';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import sql from 'react-syntax-highlighter/dist/esm/languages/hljs/sql';
import { atomOneLight, atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { format as formatSQL } from 'sql-formatter';
import EChartsRenderer, { EChartsDownloadButton } from '../../../components/EChartsRenderer';
import { downloadChartImage, ChartImageFormat } from '../../../utils/chartDownload';
import OnboardingCoachmark from '../../../components/OnboardingCoachmark';
import { useBranding } from '../../../hooks/useBranding';
import { useProductOnboarding } from '../../../hooks/useProductOnboarding';
import { useWarehouseLabels, substituteSqlLabels } from '../../../hooks/useWarehouseLabels';
import type * as echarts from 'echarts/core';
import type { QueryResultTab, ChartArtifact, MetricArtifact, DocumentArtifact, WorkspaceArtifact, SQLApproval } from '../stores/useCopilotStore';
import { useCopilotStore } from '../stores/useCopilotStore';
import { useChatStore } from '../../../chat/core/ChatStoreContext';
import { getArtifactHydrationState } from '../hooks/useArtifactHydration';
import { downloadFrame, fetchFramePage } from '../../../utils/frameExport';
import { getDocumentDownloadUrl, getPlaybookRun, getRunXlsxPreview } from '../../../services/playbooksService';
import type { DocSpec, XlsxFillSpec, XlsxWorkbookPreview } from '../../../types/playbooks';
import { DownloadButton, Modal, ToolbarButton, XlsxPreview } from '../../../ui-kit';
import DocSpecPreview from './DocSpecPreview';

SyntaxHighlighter.registerLanguage('sql', sql);

/** Read the active tenant id once at module-eval-ish time. The Copilot
 * surface is always inside an authenticated tenant scope so this is safe;
 * we still tolerate a missing value (returns null) and the labels hook
 * stays inert in that case. */
function readTenantId(): string | null {
  try {
    const raw = window.localStorage.getItem('klaris_tenant');
    if (!raw) return null;
    const t = JSON.parse(raw);
    return t?.tenant_id || t?.id || null;
  } catch { return null; }
}

// ============================================================================
// Main component
// ============================================================================

interface WorkspacePanelProps {
  artifact: WorkspaceArtifact | null;
  pendingVoiceCallApproval?: VoiceCallApproval | null;
  onVoiceCallApprove?: () => void;
  onVoiceCallEditNumber?: (editedNumber: string) => void;
  onVoiceCallReject?: (reason: string) => void;
  pendingSQLApproval?: SQLApproval | null;
  onSQLApprove?: () => void;
  onSQLEdit?: (editedSql: string) => void;
  onSQLReject?: (reason: string) => void;
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
  /** Title of the current thread (for canvas header context). */
  threadTitle?: string | null;
  /** Streaming state — drives the empty/working placeholder. */
  isStreaming?: boolean;
  streamingAction?: string;
}

export default function WorkspacePanel({
  artifact,
  pendingVoiceCallApproval,
  onVoiceCallApprove,
  onVoiceCallEditNumber,
  onVoiceCallReject,
  pendingSQLApproval,
  onSQLApprove,
  onSQLEdit,
  onSQLReject,
  onAddToDashboard,
  threadTitle,
  isStreaming,
  streamingAction,
}: WorkspacePanelProps) {
  // A pending phone call outranks everything else on screen: the graph is
  // paused on it, and nothing else the analyst does will move until they
  // answer. Centred rather than stretched — it's a decision, not an artifact.
  if (
    pendingVoiceCallApproval &&
    onVoiceCallApprove &&
    onVoiceCallEditNumber &&
    onVoiceCallReject
  ) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 520 }}>
          <VoiceCallApprovalCard
            approval={pendingVoiceCallApproval}
            onApprove={onVoiceCallApprove}
            onEditNumber={onVoiceCallEditNumber}
            onReject={onVoiceCallReject}
          />
        </Box>
      </Box>
    );
  }

  // SQL approval takes priority over regular artifacts
  if (pendingSQLApproval && onSQLApprove && onSQLEdit && onSQLReject) {
    return (
      <SQLApprovalCard
        approval={pendingSQLApproval}
        onApprove={onSQLApprove}
        onEdit={onSQLEdit}
        onReject={onSQLReject}
      />
    );
  }
  if (!artifact) return <WorkspaceEmptyState isStreaming={isStreaming} streamingAction={streamingAction} />;
  return (
    <ArtifactCanvas
      key={artifact.id}
      artifact={artifact}
      onAddToDashboard={onAddToDashboard}
      threadTitle={threadTitle}
    />
  );
}

// ============================================================================
// Artifact Canvas - scrollable card layout
// ============================================================================

export type ArtifactCanvasVariant = 'full' | 'inline';

function artifactTypeLabel(artifact: WorkspaceArtifact): string {
  switch (artifact.type) {
    case 'query_result': return 'Table';
    case 'chart': return 'Chart';
    case 'metric_card': return 'Metric';
    case 'document': return 'Document';
    case 'finding': return 'Finding';
    default: return 'Result';
  }
}

/** Compact card for mobile unified chat before tap-to-expand. */
export function ArtifactStubCard({
  artifact,
  onExpand,
}: {
  artifact: WorkspaceArtifact;
  onExpand: () => void;
}) {
  const theme = useTheme();
  return (
    <Box
      component="button"
      type="button"
      onClick={onExpand}
      sx={{
        width: '100%',
        textAlign: 'left',
        border: `1px solid ${theme.tokens.border.subtle}`,
        borderRadius: `${theme.ds.radius.md}px`,
        bgcolor: theme.tokens.surface.raised,
        px: 1.5,
        py: 1.25,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        '&:hover': { bgcolor: theme.tokens.surface.sunken },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.6875rem' }}>
          {artifactTypeLabel(artifact)}
          {artifact.type === 'query_result' && artifact.rowCount > 0
            ? ` · ${artifact.rowCount.toLocaleString()} rows`
            : ''}
        </Typography>
        <Typography variant="body2" fontWeight={600} noWrap>
          {artifact.title}
        </Typography>
      </Box>
      <Typography variant="caption" color="primary.main" sx={{ flexShrink: 0, fontWeight: 600 }}>
        Tap to load
      </Typography>
    </Box>
  );
}

export function ArtifactHydrationPlaceholder({ artifact }: { artifact: WorkspaceArtifact }) {
  const theme = useTheme();
  const isError = artifact.hydration === 'error';
  return (
    <Box
      sx={{
        border: `1px solid ${theme.tokens.border.subtle}`,
        borderRadius: `${theme.ds.radius.md}px`,
        bgcolor: theme.tokens.surface.raised,
        px: 2,
        py: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        minHeight: 120,
      }}
    >
      {isError ? (
        <Typography variant="body2" color="error.main" align="center">
          {artifact.hydrationError || 'Failed to load result'}
        </Typography>
      ) : (
        <>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Loading {artifact.title || 'result'}…
          </Typography>
        </>
      )}
    </Box>
  );
}

export function ArtifactCanvas({
  artifact,
  onAddToDashboard,
  variant = 'full',
  showSql = true,
  embedTitleInCard,
  threadTitle,
}: {
  artifact: WorkspaceArtifact;
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
  /** `inline` = bounded height for embedding in mobile unified chat */
  variant?: ArtifactCanvasVariant;
  /** When false, omit the SQL / data-source card (e.g. mobile Results artifact detail). */
  showSql?: boolean;
  /**
   * When true, artifact title + meta render inside the white card (not a separate bar above).
   * Defaults to true when `variant` is `inline`.
   */
  embedTitleInCard?: boolean;
  threadTitle?: string | null;
}) {
  const theme = useTheme();
  const inline = variant === 'inline';
  const hydration = getArtifactHydrationState(artifact);
  const inlineDocument = inline && artifact.type === 'document';
  const embedTitle = embedTitleInCard ?? inline;
  // Lifted from ChartCard so the canvas header can drive chart downloads
  // even though the ECharts instance is owned by the renderer below.
  const [chartInstance, setChartInstance] = useState<echarts.ECharts | null>(null);

  if (hydration !== 'ready') {
    return <ArtifactHydrationPlaceholder artifact={{ ...artifact, hydration }} />;
  }

  // Card styling sourced from design tokens — surface.raised on canvas,
  // hairline border, soft xs shadow.
  const cardSx = {
    border: `1px solid ${theme.tokens.border.subtle}`,
    borderRadius: `${theme.ds.radius.lg}px`,
    bgcolor: theme.tokens.surface.raised,
    boxShadow: theme.tokens.shadow.xs,
    overflow: 'hidden' as const,
  };

  // The card-level pin/download toolbar is only useful when the canvas
  // header is *not* being shown (i.e. embedded inline in mobile chat).
  // When the canvas header drives those actions, suppress them on cards.
  const showCardActions = embedTitle;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: inline ? 'auto' : '100%',
        maxHeight: inline && !inlineDocument ? 480 : undefined,
        minWidth: 0,
        animation: 'workspaceFadeIn 0.32s cubic-bezier(0.22,1,0.36,1)',
        '@keyframes workspaceFadeIn': {
          '0%': { opacity: 0, transform: 'translateY(8px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* ── Canvas header: title + meta + unified action bar ───────────────── */}
      {!embedTitle && (
        <CanvasHeader
          artifact={artifact}
          threadTitle={threadTitle}
          onAddToDashboard={onAddToDashboard}
          chartInstance={chartInstance}
        />
      )}

      {/* ── Scrollable canvas ────────────────────────────────── */}
      <Box
        sx={(t) => ({
          flex: inline ? '0 1 auto' : 1,
          minHeight: inline ? 120 : 0,
          maxHeight: inline && !inlineDocument ? 400 : undefined,
          overflow: inlineDocument ? 'visible' : 'auto',
          p: embedTitle ? 0 : { xs: 1.5, sm: 2.5 },
          display: 'flex',
          flexDirection: 'column',
          gap: embedTitle ? { xs: 1.25, sm: 1.5 } : { xs: 1.5, sm: 2 },
          bgcolor: embedTitle ? 'transparent' : t.tokens.surface.canvas,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: t.tokens.border.default,
            borderRadius: 3,
          },
        })}
      >
        {artifact.type === 'query_result' && (
          <QueryResultViewTabs
            artifact={artifact}
            cardSx={cardSx}
            onAddToDashboard={showCardActions ? onAddToDashboard : undefined}
            showSql={showSql}
            embedTitle={embedTitle}
            showCardActions={showCardActions}
          />
        )}

        {artifact.type === 'chart' && (
          <ChartCard
            artifact={artifact}
            cardSx={cardSx}
            onAddToDashboard={showCardActions ? onAddToDashboard : undefined}
            embedTitle={embedTitle}
            showCardActions={showCardActions}
            onChartInstanceChange={setChartInstance}
          />
        )}

        {artifact.type === 'metric_card' && (
          <MetricCardCard
            artifact={artifact}
            cardSx={cardSx}
            onAddToDashboard={showCardActions ? onAddToDashboard : undefined}
            embedTitle={embedTitle}
            showCardActions={showCardActions}
          />
        )}

        {artifact.type === 'document' && (
          <DocumentCard artifact={artifact} cardSx={cardSx} />
        )}

        {/* findings are shown inline in chat, not in workspace */}
      </Box>
    </Box>
  );
}

// ── DocumentCard ─────────────────────────────────────────────────────────────

interface DocumentCardProps {
  artifact: DocumentArtifact;
  cardSx?: object;
}

export function DocumentCard({ artifact, cardSx }: DocumentCardProps) {
  const theme = useTheme();
  const { notify } = React.useContext(SnackbarContext);
  const [downloading, setDownloading] = React.useState(false);
  const tenantId = readTenantId();
  const canPreview = Boolean(artifact.playbookId && artifact.runId);
  // Preview is expanded by default so the report is visible without a click.
  const [showPreview, setShowPreview] = React.useState(canPreview);
  const [spec, setSpec] = React.useState<DocSpec | XlsxFillSpec | null>(null);
  const [workbook, setWorkbook] = React.useState<XlsxWorkbookPreview | null>(null);
  const [loadingSpec, setLoadingSpec] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const [specError, setSpecError] = React.useState<string | null>(null);

  const loadSpec = React.useCallback(async () => {
    if (spec || workbook || loadingSpec || !canPreview) return;
    setLoadingSpec(true);
    setSpecError(null);
    const isXlsx = artifact.format === 'xlsx';
    try {
      // For xlsx outputs, prefer the server-rendered workbook grid (the actual
      // filled file, sheet-by-sheet). Fall back to the fill payload preview,
      // then to a download-only message.
      if (isXlsx) {
        try {
          const wb = await getRunXlsxPreview(artifact.playbookId, artifact.runId, artifact.artifactId);
          if (wb?.sheets?.length) {
            setWorkbook(wb);
            return;
          }
        } catch {
          /* fall through to the fill-payload preview */
        }
      }

      // Resolve this card's preview from the run's outputs. Match the specific
      // artifact when we know its id, else fall back to format/role.
      const run = await getPlaybookRun(artifact.playbookId, artifact.runId);
      const outputs = run.outputs || [];
      const match =
        (artifact.artifactId && outputs.find((o) => o.artifact_id === artifact.artifactId)) ||
        outputs.find((o) => o.format === (artifact.format || 'docx')) ||
        outputs[0];
      if (match?.preview) {
        setSpec(match.preview);
      } else if (isXlsx) {
        setSpecError('Preview is not available for this workbook. Use Download to open it.');
      } else {
        setSpecError('Preview is not available for this report.');
      }
    } catch (e: any) {
      setSpecError(e?.message || 'Failed to load preview.');
    } finally {
      setLoadingSpec(false);
    }
  }, [spec, workbook, loadingSpec, canPreview, artifact.playbookId, artifact.runId, artifact.format, artifact.artifactId]);

  // Fetch the DocSpec on mount when the preview starts expanded.
  React.useEffect(() => {
    if (showPreview && canPreview) void loadSpec();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTogglePreview = () => {
    const next = !showPreview;
    setShowPreview(next);
    if (next) void loadSpec();
  };

  const handleDownload = async () => {
    if (!tenantId || !artifact.playbookId || !artifact.runId || downloading) return;
    setDownloading(true);
    try {
      // Use the shared helper so we hit the absolute backend URL (buildApiUrl)
      // rather than a relative path — a relative path resolves against the
      // frontend dev server and returns index.html, corrupting the .docx.
      const url = getDocumentDownloadUrl(artifact.playbookId, artifact.runId, artifact.artifactId);
      const token = localStorage.getItem('klaris_jwt');
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      // Pick the extension from the artifact's medium (falling back to the
      // loaded spec) so xlsx fills don't download with a .docx name.
      const fmt = artifact.format || (spec as XlsxFillSpec | null)?.format || 'docx';
      const ext = fmt === 'xlsx' ? 'xlsx' : fmt === 'pptx' ? 'pptx' : fmt === 'pdf' ? 'pdf' : 'docx';
      link.download = `${artifact.title.replace(/[^a-z0-9_\-]/gi, '_').toLowerCase()}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (e: any) {
      notify(e?.message || 'Download failed', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const fileLabel =
    { xlsx: 'Excel (.xlsx)', docx: 'Word (.docx)', pptx: 'PowerPoint (.pptx)', pdf: 'PDF' }[
      artifact.format || 'docx'
    ] || 'File';

  // The kit's download control, so this card reads like every other download
  // in the product. One file, so no menu.
  const downloadControl = (
    <DownloadButton
      loading={downloading}
      disabled={!artifact.runId}
      tooltip={`Download as ${fileLabel}`}
      formats={[{ key: 'file', label: fileLabel, onDownload: handleDownload }]}
    />
  );

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.tokens.border.default}`,
        bgcolor: theme.tokens.surface.raised,
        ...cardSx,
      }}
    >
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: theme.tokens.surface.overlay,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <DescriptionRounded sx={{ color: theme.tokens.text.secondary, fontSize: 24 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" fontWeight={600} noWrap>
            {artifact.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {(artifact.format || (spec as XlsxFillSpec | null)?.format) === 'xlsx'
              ? 'Excel workbook filled by agent'
              : 'DOCX report generated by agent'}
          </Typography>
        </Box>
        {canPreview && (
          <ToolbarButton
            icon={showPreview ? <ExpandLessRounded fontSize="small" /> : <VisibilityOutlined fontSize="small" />}
            onClick={handleTogglePreview}
          >
            {showPreview ? 'Hide preview' : 'Preview'}
          </ToolbarButton>
        )}
        {workbook && showPreview && (
          <ToolbarButton icon={<OpenInFullRounded fontSize="small" />} onClick={() => setExpanded(true)}>
            Expand
          </ToolbarButton>
        )}
        {downloadControl}
      </Box>
      <Collapse in={showPreview} unmountOnExit>
        <Box sx={{ px: 3, pb: 3 }}>
          {loadingSpec ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3, justifyContent: 'center' }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading preview…
              </Typography>
            </Box>
          ) : specError ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {specError}
            </Typography>
          ) : workbook ? (
            <XlsxPreview sheets={workbook.sheets} sheetsOmitted={workbook.sheets_omitted ?? 0} />
          ) : spec ? (
            (spec as XlsxFillSpec).format === 'xlsx' ? (
              <XlsxFillPreview spec={spec as XlsxFillSpec} />
            ) : (
              <DocSpecPreview spec={spec as DocSpec} />
            )
          ) : null}
        </Box>
      </Collapse>
      <Modal
        open={expanded}
        onClose={() => setExpanded(false)}
        title={artifact.title}
        subtitle="Excel workbook"
        size="full"
        actions={downloadControl}
      >
        {workbook && (
          <XlsxPreview sheets={workbook.sheets} sheetsOmitted={workbook.sheets_omitted ?? 0} fill />
        )}
      </Modal>
    </Box>
  );
}

/**
 * Inline preview for an xlsx agent's filled template: shows the scalar values
 * and any table row-sets the agent produced. (The styled workbook itself is
 * available via Download.)
 */
function XlsxFillPreview({ spec }: { spec: XlsxFillSpec }) {
  const theme = useTheme();
  const values = spec.values || {};
  const tables = spec.tables || {};
  const valueEntries = Object.entries(values);
  const tableEntries = Object.entries(tables);

  return (
    <Box
      sx={{
        borderRadius: 1.5,
        border: `1px solid ${theme.tokens.border.default}`,
        bgcolor: '#fff',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ height: 4, bgcolor: '#1B873F' }} />
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>
          Filled into template
        </Typography>
        {valueEntries.length > 0 && (
          <Box sx={{ mt: 1, border: `1px solid ${theme.tokens.border.subtle ?? theme.tokens.border.default}`, borderRadius: 1, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <tbody>
                {valueEntries.map(([k, v], i) => (
                  <tr key={k} style={{ background: i % 2 ? 'rgba(0,0,0,0.025)' : 'transparent' }}>
                    <td style={{ padding: '6px 12px', color: theme.tokens.text.secondary, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{k}</td>
                    <td style={{ padding: '6px 12px', fontWeight: 600 }}>{v === null || v === undefined ? '' : String(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
        {tableEntries.map(([name, rows]) => {
          const cols = rows && rows.length ? Object.keys(rows[0]) : [];
          return (
            <Box key={name} sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {name} <Typography component="span" variant="caption" color="text.secondary">({rows.length} rows)</Typography>
              </Typography>
              <Box sx={{ border: `1px solid ${theme.tokens.border.subtle ?? theme.tokens.border.default}`, borderRadius: 1, overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'rgba(0,0,0,0.04)' }}>
                      {cols.map((c) => (
                        <th key={c} style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 700 }}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 50).map((row, ri) => (
                      <tr key={ri} style={{ background: ri % 2 ? 'rgba(0,0,0,0.025)' : 'transparent' }}>
                        {cols.map((c) => (
                          <td key={c} style={{ padding: '6px 12px' }}>{row[c] === null || row[c] === undefined ? '' : String(row[c])}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Box>
          );
        })}
        {valueEntries.length === 0 && tableEntries.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No filled values recorded. Use Download to open the workbook.
          </Typography>
        )}
      </Box>
    </Box>
  );
}


/**
 * Format a query/compute duration for the meta line. Returns null for
 * unknown / non-positive values so callers can omit the chip entirely — a
 * literal "0ms" on a replayed artifact reads like a bug and erodes trust.
 */
function formatElapsed(ms: number | undefined | null): string | null {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return null;
  if (ms < 1) return '<1 ms';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  return `${s < 10 ? s.toFixed(1) : Math.round(s)} s`;
}

// ============================================================================
// Canvas Header — Q. <question> + session meta + action bar
// ============================================================================

function CanvasHeader({
  artifact,
  threadTitle,
  onAddToDashboard,
  chartInstance,
}: {
  artifact: WorkspaceArtifact;
  threadTitle?: string | null;
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
  chartInstance?: echarts.ECharts | null;
}) {
  const [linkCopied, setLinkCopied] = useState(false);
  const [downloadAnchor, setDownloadAnchor] = useState<HTMLElement | null>(null);
  const [downloading, setDownloading] = useState(false);
  const { notify } = React.useContext(SnackbarContext);
  const headerThreadId = useChatStore((s) => s.threadId);
  // On narrow canvases the meta line gets cramped; the thread title is the
  // lowest-priority part (also visible in the sidebar) so we drop it first.
  const isNarrow = useMediaQuery((t: any) => t.breakpoints.down('sm'));

  // Onboarding coach-mark for the Pin to Dashboard button. Anchored to the
  // canvas-header pin button (the inline card button on desktop is hidden via
  // `showCardActions=false`, so the visual nudge has to live up here now).
  const { currentStepId, shouldShow: onboardingActive } = useProductOnboarding();
  const [dismissPinNudge, setDismissPinNudge] = useState(false);
  const pinBtnRef = useRef<HTMLButtonElement | null>(null);
  const showPinCoachmark =
    onboardingActive &&
    currentStepId === 'widget_added' &&
    !dismissPinNudge &&
    !!onAddToDashboard;

  // Headline = the AI-generated artifact title, e.g. "Leads Distribution by Source".
  // We deliberately do not show the originating user question here — users can
  // always read it in the chat panel, and the artifact title is the unique
  // identifier for what's on the canvas.
  const headline = artifact.title;

  // Meta line — small, dot-separated, derived from artifact + thread.
  const metaParts: Array<React.ReactNode> = [];
  // Thread title is context, not a second headline — prefix it with a chat
  // glyph so it reads as "from this chat", and drop it first when space is tight.
  if (threadTitle && !isNarrow) {
    metaParts.push(
      <Box key="thread" component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.375, minWidth: 0 }}>
        <ChatBubbleOutlineRounded sx={{ fontSize: 11, opacity: 0.8 }} />
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
          {threadTitle}
        </Box>
      </Box>
    );
  }
  if (artifact.createdAt) {
    metaParts.push(
      <Box
        key="time"
        component="span"
        title={formatAbsolute(artifact.createdAt)}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.375 }}
      >
        <AccessTimeRounded sx={{ fontSize: 11 }} />
        {formatRelativeTime(artifact.createdAt)}
      </Box>
    );
  }
  if (artifact.type === 'query_result') {
    if (artifact.connectorName) {
      metaParts.push(
        <Box key="conn" component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.375 }}>
          <StorageRounded sx={{ fontSize: 11 }} />
          {artifact.connectorName}
        </Box>
      );
    }
    metaParts.push(<span key="rows">{`${artifact.rowCount.toLocaleString()} rows`}</span>);
    const elapsed = formatElapsed(artifact.elapsedMs);
    if (elapsed) {
      metaParts.push(<span key="elapsed">{elapsed}</span>);
    }
  } else if (artifact.type === 'chart') {
    metaParts.push(<span key="type">{artifact.chartType}</span>);
    metaParts.push(<span key="rows">{`${artifact.rowCount.toLocaleString()} rows`}</span>);
  } else if (artifact.type === 'metric_card') {
    metaParts.push(<span key="metric">{artifact.metricName}</span>);
  } else if (artifact.type === 'document') {
    metaParts.push(<span key="doctype">{(artifact.format || 'docx').toUpperCase()}</span>);
  }

  const handleCopyLink = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      params.set('artifact', artifact.id);
      const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  // ── Download wiring (artifact-aware) ───────────────────────────────────
  // Tables download server-side (full dataset, CSV/XLSX) and require a live
  // frame reference; legacy replayed artifacts without one show no download.
  const tableFrameId =
    artifact.type === 'query_result' ? (artifact as QueryResultTab).frameId : undefined;
  const canDownloadTable = Boolean(tableFrameId && headerThreadId);
  const canDownloadChart = artifact.type === 'chart' && !!chartInstance;
  const canDownload = canDownloadTable || canDownloadChart;

  const handleDownloadClick = (e: React.MouseEvent<HTMLElement>) => {
    // Both tables (CSV/XLSX) and charts (PNG/JPEG/SVG) open a format menu.
    if (canDownload) setDownloadAnchor(e.currentTarget);
  };

  const handleTableDownload = async (format: 'csv' | 'xlsx') => {
    setDownloadAnchor(null);
    if (!tableFrameId || !headerThreadId || downloading) return;
    const label = format === 'xlsx' ? 'Excel' : 'CSV';
    setDownloading(true);
    notify(`Preparing ${label} download\u2026`, 'info');
    try {
      const { filename } = await downloadFrame(tableFrameId, headerThreadId, format, artifact.title);
      notify(`Downloaded ${filename}`, 'success');
    } catch (e: any) {
      notify(e?.message || `Failed to download ${label}`, 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleChartDownload = (format: ChartImageFormat) => {
    try {
      downloadChartImage(chartInstance, format, artifact.title);
    } catch (e: any) {
      notify(e?.message || 'Could not download the chart.', 'error');
    } finally {
      setDownloadAnchor(null);
    }
  };

  const iconBtnSx = (t: any) => ({
    width: 30,
    height: 30,
    color: t.tokens.text.tertiary,
    '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.surface.sunken },
  });

  return (
    <Box
      sx={(t) => ({
        flexShrink: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.375,
        px: { xs: 2, sm: 2.5 },
        py: 1.25,
        borderBottom: `1px solid ${t.tokens.border.subtle}`,
        bgcolor: t.tokens.surface.raised,
      })}
    >
      {/* Top row: artifact title + unified action bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            title={headline}
            sx={(t) => ({
              fontWeight: 600,
              fontSize: '0.9375rem',
              letterSpacing: '-0.005em',
              lineHeight: 1.4,
              color: t.tokens.text.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
            })}
          >
            {headline}
          </Typography>
        </Box>

        {/* Unified action bar — all artifact-level CTAs live here */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
          {canDownload && (
            <Tooltip
              title={downloading ? 'Preparing download\u2026' : (canDownloadTable ? 'Download' : 'Download chart')}
              placement="bottom"
            >
              <span>
                <IconButton
                  size="small"
                  onClick={handleDownloadClick}
                  disabled={downloading}
                  aria-label={canDownloadTable ? 'Download data' : 'Download chart'}
                  aria-haspopup="menu"
                  sx={(t: any) => ({
                    height: 30,
                    width: 'auto',
                    px: 0.625,
                    borderRadius: `${t.ds.radius.sm}px`,
                    color: t.tokens.text.tertiary,
                    '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.surface.sunken },
                  })}
                >
                  {downloading
                    ? <CircularProgress size={15} thickness={5} sx={{ color: 'inherit' }} />
                    : <DownloadRounded sx={{ fontSize: 16 }} />}
                  {!downloading && (
                    <ArrowDropDownRounded sx={{ fontSize: 16, ml: -0.125, opacity: 0.7 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title={linkCopied ? 'Link copied' : 'Copy link'} placement="bottom">
            <IconButton size="small" onClick={handleCopyLink} aria-label="Copy link to this artifact" sx={iconBtnSx}>
              {linkCopied
                ? <CheckRounded sx={(t) => ({ fontSize: 16, color: t.tokens.semantic.success })} />
                : <LinkRounded sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>

          {/* Pin to dashboard — labeled because it's the primary save action */}
          {onAddToDashboard && (
            <>
              <Divider
                orientation="vertical"
                flexItem
                sx={(t) => ({ mx: 0.5, my: 0.5, borderColor: t.tokens.border.subtle })}
              />
              <Tooltip title="Pin to dashboard" placement="bottom">
                <Button
                  ref={pinBtnRef}
                  size="small"
                  variant="outlined"
                  startIcon={<DashboardCustomizeRounded sx={{ fontSize: 14 }} />}
                  onClick={() => {
                    // Dismiss the coach-mark immediately so the spotlight halo
                    // doesn't linger over the now-clicked button while the
                    // dashboard-picker modal animates in.
                    setDismissPinNudge(true);
                    onAddToDashboard(artifact);
                  }}
                  sx={(t) => ({
                    py: 0.375,
                    px: 1,
                    height: 30,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: `${t.ds.radius.sm}px`,
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    borderColor: showPinCoachmark ? t.tokens.brand.primary : t.tokens.border.default,
                    color: showPinCoachmark ? t.tokens.brand.primary : t.tokens.text.secondary,
                    bgcolor: showPinCoachmark ? t.tokens.brand.primarySoft : 'transparent',
                    '&:hover': {
                      borderColor: t.tokens.brand.primary,
                      color: t.tokens.brand.primary,
                      bgcolor: t.tokens.brand.primarySoft,
                    },
                    animation: showPinCoachmark ? 'pinPulseHeader 1.6s ease-in-out infinite' : 'none',
                    '@keyframes pinPulseHeader': {
                      '0%':   { transform: 'scale(1)' },
                      '50%':  { transform: 'scale(1.03)' },
                      '100%': { transform: 'scale(1)' },
                    },
                  })}
                >
                  Pin to dashboard
                </Button>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      <OnboardingCoachmark
        open={showPinCoachmark}
        anchorRef={pinBtnRef}
        placement="bottom-end"
        message="Pin this chart to your dashboard."
      />

      {/* Meta line (session · time · source · rows) */}
      {metaParts.length > 0 && (
        <Box
          sx={(t) => ({
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.5,
            rowGap: 0.25,
            fontSize: '0.75rem',
            color: t.tokens.text.tertiary,
            fontVariantNumeric: 'tabular-nums',
            minWidth: 0,
          })}
        >
          {metaParts.map((part, i) => (
            <React.Fragment key={i}>
              {i > 0 && <Box component="span" sx={{ opacity: 0.5 }}>·</Box>}
              {part}
            </React.Fragment>
          ))}
        </Box>
      )}

      {/* Chart download format menu */}
      {canDownloadChart && (
        <ChartDownloadMenu
          anchorEl={downloadAnchor}
          onClose={() => setDownloadAnchor(null)}
          onPick={handleChartDownload}
        />
      )}

      {/* Table download format menu (server-side full dataset) */}
      {canDownloadTable && (
        <Menu
          anchorEl={downloadAnchor}
          open={Boolean(downloadAnchor)}
          onClose={() => setDownloadAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { mt: 0.5, minWidth: 200 } }}
        >
          <MenuItem onClick={() => handleTableDownload('xlsx')}>
            <ListItemText primary="Download as Excel (.xlsx)" primaryTypographyProps={{ fontSize: '0.8125rem' }} />
          </MenuItem>
          <MenuItem onClick={() => handleTableDownload('csv')}>
            <ListItemText primary="Download as CSV" primaryTypographyProps={{ fontSize: '0.8125rem' }} />
          </MenuItem>
        </Menu>
      )}
    </Box>
  );
}

// Lightweight format picker so we don't bring in the heavier
// EChartsDownloadButton (which has its own anchor & icon).
function ChartDownloadMenu({
  anchorEl,
  onClose,
  onPick,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onPick: (format: 'png' | 'jpeg' | 'svg') => void;
}) {
  const open = Boolean(anchorEl);
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{ sx: { mt: 0.5, minWidth: 160 } }}
    >
      <MenuItem onClick={() => onPick('png')}>
        <ListItemText primary="Download as PNG" primaryTypographyProps={{ fontSize: '0.8125rem' }} />
      </MenuItem>
      <MenuItem onClick={() => onPick('jpeg')}>
        <ListItemText primary="Download as JPEG" primaryTypographyProps={{ fontSize: '0.8125rem' }} />
      </MenuItem>
      <MenuItem onClick={() => onPick('svg')}>
        <ListItemText primary="Download as SVG" primaryTypographyProps={{ fontSize: '0.8125rem' }} />
      </MenuItem>
    </Menu>
  );
}

// ============================================================================
// Query Result view tabs — switch between Table view and SQL/Source view
// ============================================================================

function QueryResultViewTabs({
  artifact,
  cardSx,
  onAddToDashboard,
  showSql,
  embedTitle,
  showCardActions = false,
}: {
  artifact: QueryResultTab;
  cardSx: Record<string, any>;
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
  showSql: boolean;
  embedTitle: boolean;
  showCardActions?: boolean;
}) {
  type ViewMode = 'table' | 'sql';
  const [view, setView] = useState<ViewMode>('table');

  const isFile = artifact.isFileConnector || artifact.connectorCategory === 'files';
  // API/REST connectors (HubSpot, Shopify, Salesforce, Gong, …) issue JSON request
  // specs and the backend tags them as `connector_category === 'api'` (or 'rest'
  // historically). The fallback allow-list catches older threads where the field
  // wasn't sent.
  //
  // IMPORTANT: connectors that replicate to the warehouse (Airtable today;
  // HubSpot/Shopify next) are SQL queries against Redshift — never list them
  // here. Source of truth is `_API_CONNECTOR_TYPES` in
  // backend/agents/copilot/tools/sql_tools.py. Keep the two in lockstep.
  const apiConnectorTypes = new Set([
    'hubspot', 'shopify', 'salesforce', 'gong', 'stripe', 'notion', 'zendesk',
  ]);
  const isApi = !isFile && (
    artifact.connectorCategory === 'api'
    || artifact.connectorCategory === 'rest'
    || apiConnectorTypes.has(artifact.connectorType)
  );
  const sqlTabLabel = isFile ? 'Source' : isApi ? 'Request' : 'SQL';

  const tabBtnSx = (active: boolean) => (t: any) => ({
    position: 'relative',
    px: 1.25,
    py: 0.625,
    fontSize: '0.75rem',
    fontWeight: active ? 700 : 600,
    letterSpacing: '0.005em',
    color: active ? t.tokens.text.primary : t.tokens.text.tertiary,
    bgcolor: 'transparent',
    borderRadius: `${t.ds.radius.sm}px`,
    cursor: 'pointer',
    border: 0,
    fontFamily: 'inherit',
    transition: `color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}, background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
    '&:hover': {
      color: t.tokens.text.primary,
      bgcolor: active ? 'transparent' : t.tokens.surface.sunken,
    },
    '&::after': active ? {
      content: '""',
      position: 'absolute',
      left: 8,
      right: 8,
      bottom: -1,
      height: 2,
      borderRadius: 1,
      bgcolor: t.tokens.brand.primary,
    } : {},
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1.25, sm: 1.5 },
      }}
    >
      {/* Tab strip */}
      {showSql && (
        <Box
          sx={(t) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            alignSelf: 'flex-start',
            borderBottom: `1px solid ${t.tokens.border.subtle}`,
            px: 0.25,
          })}
        >
          <Box
            component="button"
            onClick={() => setView('table')}
            sx={tabBtnSx(view === 'table')}
            aria-pressed={view === 'table'}
          >
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <TableChartOutlined sx={{ fontSize: 13 }} />
              Table
              <Box component="span" sx={(t: any) => ({ fontWeight: 500, color: t.tokens.text.tertiary, fontSize: '0.6875rem' })}>
                {`(${artifact.rowCount.toLocaleString()})`}
              </Box>
            </Box>
          </Box>
          <Box
            component="button"
            onClick={() => setView('sql')}
            sx={tabBtnSx(view === 'sql')}
            aria-pressed={view === 'sql'}
          >
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <CodeRounded sx={{ fontSize: 13 }} />
              {sqlTabLabel}
            </Box>
          </Box>
        </Box>
      )}

      {/* Content */}
      {(!showSql || view === 'table') && (
        <DataTableCard
          artifact={artifact}
          cardSx={cardSx}
          onAddToDashboard={onAddToDashboard}
          embedTitle={embedTitle}
          showCardActions={showCardActions}
        />
      )}

      {showSql && view === 'sql' && (
        <SQLCard
          sql={artifact.sql}
          cardSx={cardSx}
          isFileConnector={isFile}
          isApiConnector={isApi}
          connectorName={artifact.connectorName}
          title={artifact.title}
          resultColumns={artifact.columns}
          defaultExpanded
        />
      )}
    </Box>
  );
}

/** Title + meta inside the card shell (unified mobile chat / compact artifact views). */
function ArtifactInlineTitleBlock({ artifact }: { artifact: WorkspaceArtifact }) {
  return (
    <Box
      sx={(t) => ({
        px: 1.75,
        pt: 1.25,
        pb: 1.25,
        borderBottom: `1px solid ${t.tokens.border.subtle}`,
        bgcolor: t.tokens.surface.raised,
      })}
    >
      <Typography
        sx={(t) => ({
          fontSize: '0.9375rem',
          fontWeight: 600,
          letterSpacing: '-0.005em',
          lineHeight: 1.4,
          mb: artifact.type === 'query_result' ? 0.875 : 0,
          color: t.tokens.text.primary,
          wordBreak: 'break-word',
        })}
      >
        {artifact.title}
      </Typography>
      {artifact.type === 'query_result' && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
          <MetaBadge icon={<StorageRounded sx={{ fontSize: 12 }} />} label={artifact.connectorName} />
          <MetaBadge label={`${artifact.rowCount.toLocaleString()} rows`} />
          {formatElapsed(artifact.elapsedMs) && (
            <MetaBadge label={formatElapsed(artifact.elapsedMs)!} muted />
          )}
        </Box>
      )}
    </Box>
  );
}

// ============================================================================
// SQL Card - collapsed to 1-line preview by default
// ============================================================================

function SQLCard({
  sql: rawSql,
  cardSx,
  isFileConnector = false,
  isApiConnector = false,
  connectorName = '',
  title = '',
  resultColumns = [],
  defaultExpanded = false,
}: {
  sql: string;
  cardSx: Record<string, any>;
  isFileConnector?: boolean;
  isApiConnector?: boolean;
  connectorName?: string;
  title?: string;
  resultColumns?: string[];
  defaultExpanded?: boolean;
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const codeBg = theme.tokens.surface.sunken;
  const lineNumberColor = theme.tokens.text.tertiary;
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [copied, setCopied] = useState(false);
  const isNonSql = isFileConnector || isApiConnector;

  // Friendly-label substitution for replicated warehouse tables. Pure
  // render-layer transformation — the underlying `rawSql` is what the
  // backend actually executed, kept untouched. Labels missing from the
  // map (e.g. non-replicating connector or before the map loads) leave
  // the corresponding FQN as-is, so the SQL never becomes more confusing
  // than it already is.
  const tenantId = readTenantId();
  const { labels } = useWarehouseLabels(isNonSql ? null : tenantId);
  const displaySql = useMemo(
    () => (isNonSql ? rawSql : substituteSqlLabels(rawSql, labels)),
    [rawSql, labels, isNonSql],
  );

  // Parse file connector JSON for clean display
  let fileEntityName = '';
  let fileColumns: string[] = [];
  if (isFileConnector) {
    try {
      const q = JSON.parse(rawSql);
      // Show human-friendly name: extract sheet/table name from entity_id (format: "fileId:SheetName")
      const eid = q.entity_id || '';
      const sheetName = eid.includes(':') ? eid.split(':').slice(1).join(':') : '';
      fileEntityName = sheetName || title || eid;
      // Use query-specified columns, fall back to actual result columns
      const queryCols = q.columns || [];
      fileColumns = queryCols.length > 0 ? queryCols : resultColumns;
    } catch { /* noop */ }
  }

  let apiResource = '';
  let apiProperties: string[] = [];
  if (isApiConnector) {
    try {
      const q = JSON.parse(rawSql);
      apiResource = q.resource || title || 'API resource';
      apiProperties = Array.isArray(q.properties) ? q.properties : [];
    } catch { /* noop */ }
  }

  let formattedSQL = displaySql;
  if (!isNonSql) {
    try { formattedSQL = formatSQL(displaySql, { language: 'postgresql' }); } catch { /* noop */ }
  }

  // 1-line preview
  const previewLine = isFileConnector
    ? (fileEntityName || 'File data source')
    : isApiConnector
      ? (apiResource || 'API request')
    : (formattedSQL.split('\n').find((l) => l.trim().length > 0) || displaySql.slice(0, 120));

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Per design: copy what's visible. Users never see raw normalized
    // FQNs in the UI, so copying them would be more confusing than the
    // friendly form. Falls back to rawSql for non-SQL connectors.
    navigator.clipboard.writeText(isNonSql ? rawSql : formattedSQL);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box sx={{ ...cardSx, ...(isNonSql ? { overflow: 'visible' } : {}) }}>
      {/* Header row - always visible */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={(t) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 0.875,
          px: 1.75,
          py: 1.125,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { bgcolor: t.tokens.sidebar.itemHoverBg },
        })}
      >
        <CodeRounded sx={(t) => ({ fontSize: 16, color: t.tokens.text.tertiary, flexShrink: 0 })} />
        <Typography
          sx={(t) => ({
            ...t.typography.eyebrow,
            color: t.tokens.text.tertiary,
            flexShrink: 0,
          })}
        >
          {isFileConnector ? 'Data Source' : isApiConnector ? 'Query' : 'SQL'}
        </Typography>
        {isNonSql && connectorName && (
          <Typography
            component="span"
            sx={(t) => ({
              ml: 'auto',
              px: 1,
              py: 0.375,
              borderRadius: `${t.ds.radius.full}px`,
              bgcolor: t.tokens.surface.sunken,
              fontSize: '0.75rem',
              fontWeight: 500,
              color: t.tokens.text.secondary,
              flexShrink: 0,
            })}
          >
            {connectorName}
          </Typography>
        )}

        {/* 1-line preview when collapsed */}
        {!expanded && (
          <Typography
            noWrap
            title={previewLine}
            sx={(t) => ({
              flex: '1 1 0%',
              minWidth: 0,
              fontSize: '0.8125rem',
              fontFamily: isNonSql ? 'inherit' : t.typography.fontFamilyMono,
              color: t.tokens.text.secondary,
              ml: 0.5,
            })}
          >
            {previewLine}
          </Typography>
        )}
        {expanded && <Box sx={{ flex: 1 }} />}

        <Tooltip title={copied ? 'Copied!' : isApiConnector ? 'Copy query' : isFileConnector ? 'Copy request' : 'Copy SQL'} placement="top">
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={(t) => ({
              width: 28, height: 28,
              color: copied ? t.tokens.semantic.success : t.tokens.text.tertiary,
              '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.surface.sunken },
            })}
          >
            {copied ? <CheckRounded sx={{ fontSize: 14 }} /> : <ContentCopyRounded sx={{ fontSize: 14 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title={expanded ? 'Collapse' : 'Expand'} placement="top">
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={(t) => ({
              width: 28, height: 28,
              color: t.tokens.text.tertiary,
              '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.surface.sunken },
            })}
          >
            {expanded
              ? <UnfoldLessRounded sx={{ fontSize: 16 }} />
              : <UnfoldMoreRounded sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Expanded body */}
      {expanded && (
        <Box
          sx={(t) => ({
            px: 1,
            pb: 1,
            maxHeight: isNonSql ? 'none' : 280,
            overflow: 'auto',
            borderTop: `1px solid ${t.tokens.border.subtle}`,
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: t.tokens.border.default,
              borderRadius: 2,
            },
          })}
        >
          {isFileConnector ? (
            <Box sx={{ px: 1.5, py: 1.5 }}>
              <Typography sx={(t) => ({ fontWeight: 600, fontSize: '0.875rem', color: t.tokens.text.primary })}>
                {fileEntityName || 'Unknown file'}
              </Typography>
              {fileColumns.length > 0 && (
                <Box sx={{ mt: 1.25 }}>
                  <Typography
                    sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary, display: 'block', mb: 0.625 })}
                  >
                    Columns ({fileColumns.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {fileColumns.map((col, i) => (
                      <Typography
                        key={i}
                        component="span"
                        sx={(t) => ({
                          px: 0.875,
                          py: 0.25,
                          borderRadius: `${t.ds.radius.xs}px`,
                          bgcolor: t.tokens.surface.sunken,
                          fontFamily: t.typography.fontFamilyMono,
                          fontSize: '0.75rem',
                          color: t.tokens.text.secondary,
                        })}
                      >
                        {col}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          ) : isApiConnector ? (
            <Box sx={{ px: 1.5, py: 1.5 }}>
              <Typography sx={(t) => ({ fontWeight: 600, fontSize: '0.875rem', color: t.tokens.text.primary })}>
                {apiResource || 'Unknown resource'}
              </Typography>
              {apiProperties.length > 0 && (
                <Box sx={{ mt: 1.25 }}>
                  <Typography
                    sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary, display: 'block', mb: 0.625 })}
                  >
                    Properties ({apiProperties.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {apiProperties.map((prop, i) => (
                      <Typography
                        key={i}
                        component="span"
                        sx={(t) => ({
                          px: 0.875,
                          py: 0.25,
                          borderRadius: `${t.ds.radius.xs}px`,
                          bgcolor: t.tokens.surface.sunken,
                          fontFamily: t.typography.fontFamilyMono,
                          fontSize: '0.75rem',
                          color: t.tokens.text.secondary,
                        })}
                      >
                        {prop}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
              <Box
                sx={(t) => ({
                  mt: 1.25,
                  p: 1.25,
                  borderRadius: `${t.ds.radius.sm}px`,
                  bgcolor: t.tokens.surface.sunken,
                  border: `1px solid ${t.tokens.border.subtle}`,
                  fontFamily: t.typography.fontFamilyMono,
                  fontSize: '0.75rem',
                  lineHeight: 1.65,
                  color: t.tokens.text.primary,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                })}
              >
                {rawSql}
              </Box>
            </Box>
          ) : (
            <SyntaxHighlighter
              language="sql"
              style={isDark ? atomOneDark : atomOneLight}
              showLineNumbers
              lineNumberStyle={{
                minWidth: '2.4em',
                paddingRight: '1em',
                color: lineNumberColor,
                fontSize: '0.75rem',
                userSelect: 'none',
              }}
              customStyle={{
                margin: 0,
                padding: '12px',
                borderRadius: theme.ds.radius.sm,
                fontSize: '0.8125rem',
                lineHeight: 1.65,
                background: codeBg,
                fontFamily: theme.typography.fontFamilyMono as string,
              }}
            >
              {formattedSQL}
            </SyntaxHighlighter>
          )}
        </Box>
      )}
    </Box>
  );
}

// ============================================================================
// Data Table Card - auto-height DataGrid in a card
// ============================================================================

function DataTableCard({
  artifact,
  cardSx,
  onAddToDashboard,
  embedTitle = false,
  showCardActions = true,
}: {
  artifact: QueryResultTab;
  cardSx: Record<string, any>;
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
  /** When true, artifact title + meta are the top section of this card */
  embedTitle?: boolean;
  /** When false, the canvas header drives pin/download — hide them on the card. */
  showCardActions?: boolean;
}) {
  const threadId = useChatStore((s) => s.threadId);
  const frameId = artifact.frameId;
  // Pagination + download stream from the server and require a live frame
  // reference. Legacy replayed artifacts without one simply show no download.
  const canUseServer = Boolean(frameId && threadId);

  const fetchPage = canUseServer
    ? async (page: number, pageSize: number) => fetchFramePage(frameId!, threadId!, page, pageSize)
    : undefined;

  const handleDownload = canUseServer
    ? async (format: 'csv' | 'xlsx') => {
        await downloadFrame(frameId!, threadId!, format, artifact.title);
      }
    : undefined;

  return (
    <Box sx={cardSx}>
      {embedTitle && <ArtifactInlineTitleBlock artifact={artifact} />}
      <SmartTable
        columns={artifact.columns}
        rows={artifact.rows}
        rowCount={artifact.rowCount}
        truncated={artifact.truncated}
        hints={{
          columnTypes: artifact.columnTypes,
          columnSemantics: artifact.columnSemantics,
        }}
        variant="embed"
        showToolbar={showCardActions}
        title="Results"
        onFetchPage={fetchPage}
        onDownload={handleDownload}
        onAddToDashboard={onAddToDashboard ? () => onAddToDashboard(artifact) : undefined}
      />
    </Box>
  );
}

// ============================================================================
// Chart Card - renders ECharts spec inside a card shell
// ============================================================================

function ChartCard({
  artifact,
  cardSx,
  onAddToDashboard,
  embedTitle = false,
  showCardActions = true,
  onChartInstanceChange,
}: {
  artifact: ChartArtifact;
  cardSx: Record<string, any>;
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
  embedTitle?: boolean;
  /** When false, the canvas header drives pin/download — hide them on the card. */
  showCardActions?: boolean;
  /** Notify the parent canvas of the live ECharts instance, so the header can drive downloads. */
  onChartInstanceChange?: (instance: echarts.ECharts | null) => void;
}) {
  const { currentStepId, shouldShow } = useProductOnboarding();
  const showPinNudge = shouldShow && currentStepId === 'widget_added';
  const [dismissPinNudge, setDismissPinNudge] = useState(false);
  const pinBtnRef = useRef<HTMLButtonElement | null>(null);
  const pinBtnExpandedRef = useRef<HTMLButtonElement | null>(null);
  const showPinCoachmark = showPinNudge && !dismissPinNudge;
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const { branding } = useBranding();
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const expandInstanceRef = useRef<echarts.ECharts | null>(null);
  const [expanded, setExpanded] = useState(false);

  const handleChartReady = useCallback((instance: echarts.ECharts) => {
    chartInstanceRef.current = instance;
    onChartInstanceChange?.(instance);
  }, [onChartInstanceChange]);

  const handleExpandChartReady = useCallback((instance: echarts.ECharts) => {
    expandInstanceRef.current = instance;
  }, []);

  // When this ChartCard is unmounted (e.g. switching artifacts) clear out the
  // canvas-header reference so it doesn't keep the old instance around.
  React.useEffect(() => {
    return () => {
      onChartInstanceChange?.(null);
    };
  }, [onChartInstanceChange]);

  // Adaptive height: scale with viewport so charts fill the workspace panel
  // rather than leaving dead zones on larger screens.
  const chartHeight = useMemo(() => {
    const spec = artifact.spec?._chartHeight ?? 420;
    if (isXs) {
      const cap = typeof window !== 'undefined' ? Math.round(window.innerHeight * 0.42) : 300;
      return Math.min(spec, Math.max(220, cap));
    }
    // Desktop: use the larger of the backend-computed spec height or 55% of viewport
    // so that charts fill the available workspace panel space on bigger screens.
    const viewportBased = typeof window !== 'undefined' ? Math.round(window.innerHeight * 0.55) : 420;
    return Math.min(Math.max(spec, viewportBased), 820);
  }, [artifact.spec?._chartHeight, isXs]);
  const getPinAnchorElement = useCallback(
    () => (expanded ? pinBtnExpandedRef.current : pinBtnRef.current),
    [expanded],
  );
  const iconBtnSx = (t: any) => ({
    width: 28,
    height: 28,
    color: t.tokens.text.tertiary,
    '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.surface.sunken },
  });

  return (
    <>
      <Box sx={cardSx}>
        {embedTitle && <ArtifactInlineTitleBlock artifact={artifact} />}
        {/* Card header — only when the canvas header isn't already showing
            Download/Pin/Full-screen (i.e. embedded inline in mobile chat). */}
        {showCardActions && (
          <Box
            sx={(t) => ({
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.875,
              rowGap: 0.5,
              px: 1.75,
              py: 1,
              borderBottom: `1px solid ${t.tokens.border.subtle}`,
            })}
          >
            <BarChartRounded sx={(t) => ({ fontSize: 16, color: t.tokens.text.tertiary })} />
            <Typography sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary })}>
              Chart
            </Typography>
            <Box sx={{ flex: 1, minWidth: 40 }} />
            <Typography
              sx={(t) => ({
                fontSize: '0.75rem',
                color: t.tokens.text.tertiary,
                fontVariantNumeric: 'tabular-nums',
                mr: 0.5,
              })}
            >
              {artifact.rowCount.toLocaleString()} rows
            </Typography>

            <Divider orientation="vertical" flexItem sx={(t) => ({ mx: 0.25, borderColor: t.tokens.border.subtle })} />

            {/* Expand */}
            <Tooltip title="Expand" placement="top">
              <IconButton size="small" onClick={() => setExpanded(true)} sx={iconBtnSx}>
                <OpenInFullRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            {/* Download PNG/JPEG/SVG */}
            <EChartsDownloadButton chartInstance={chartInstanceRef.current} size="small" title={artifact.title} />

            {/* Add to dashboard - labeled button so it's clearly discoverable */}
            {onAddToDashboard && (
              <>
                <Divider orientation="vertical" flexItem sx={(t) => ({ mx: 0.5, borderColor: t.tokens.border.subtle })} />
                <Button
                  ref={pinBtnRef}
                  size="small"
                  variant="outlined"
                  startIcon={<DashboardCustomizeRounded sx={{ fontSize: 14 }} />}
                  onClick={() => { setDismissPinNudge(true); onAddToDashboard(artifact); }}
                  sx={(t) => ({
                    py: 0.375,
                    px: 1,
                    height: 28,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: `${t.ds.radius.sm}px`,
                    lineHeight: 1.5,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    borderColor: showPinCoachmark ? t.tokens.brand.primary : t.tokens.border.default,
                    color: showPinCoachmark ? t.tokens.brand.primary : t.tokens.text.secondary,
                    bgcolor: 'transparent',
                    '&:hover': {
                      borderColor: t.tokens.brand.primary,
                      color: t.tokens.brand.primary,
                      bgcolor: t.tokens.brand.primarySoft,
                    },
                    boxShadow: showPinCoachmark ? t.tokens.shadow.focusRing : 'none',
                    animation: showPinCoachmark && !isXs ? 'pinPulseChart 1.6s ease-in-out infinite' : 'none',
                    '@keyframes pinPulseChart': {
                      '0%':   { transform: 'scale(1)',   boxShadow: t.tokens.shadow.focusRing },
                      '70%':  { transform: 'scale(1.02)', boxShadow: '0 0 0 6px transparent' },
                      '100%': { transform: 'scale(1)',   boxShadow: '0 0 0 0 transparent' },
                    },
                  })}
                >
                  Pin to Dashboard
                </Button>
              </>
            )}
          </Box>
        )}
        {showCardActions && (
          <OnboardingCoachmark
            open={showPinCoachmark}
            getAnchorElement={getPinAnchorElement}
            placement="bottom-end"
            message={isXs ? 'Pin to your dashboard.' : 'Pin this chart to your dashboard.'}
          />
        )}

        {/* ECharts canvas */}
        <EChartsRenderer
          option={artifact.spec}
          branding={branding}
          height={chartHeight}
          onChartReady={handleChartReady}
        />
      </Box>

      <AppDialog
        open={expanded}
        onClose={() => setExpanded(false)}
        size="lg"
        icon={<BarChartRounded sx={(t) => ({ fontSize: 16, color: t.tokens.text.tertiary })} />}
        title={artifact.title}
        headerExtras={
          <>
            <EChartsDownloadButton chartInstance={expandInstanceRef.current} size="small" title={artifact.title} />
            {onAddToDashboard && (
              <Button
                ref={pinBtnExpandedRef}
                size="small"
                variant="outlined"
                startIcon={<DashboardCustomizeRounded sx={{ fontSize: 14 }} />}
                onClick={() => { setDismissPinNudge(true); onAddToDashboard(artifact); setExpanded(false); }}
                sx={(t) => ({
                  py: 0.375,
                  px: 1,
                  height: 30,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: `${t.ds.radius.sm}px`,
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  borderColor: t.tokens.border.default,
                  color: t.tokens.text.secondary,
                  '&:hover': {
                    borderColor: t.tokens.brand.primary,
                    color: t.tokens.brand.primary,
                    bgcolor: t.tokens.brand.primarySoft,
                  },
                })}
              >
                Pin to Dashboard
              </Button>
            )}
          </>
        }
        paperSx={{ width: '90vw', maxWidth: '90vw !important', height: '85vh' }}
        contentSx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
          <EChartsRenderer
            option={artifact.spec}
            branding={branding}
            fillContainer
            onChartReady={handleExpandChartReady}
          />
      </AppDialog>
    </>
  );
}

// ============================================================================
// Metric Card Card - renders a single KPI metric inside the workspace panel
// ============================================================================

function MetricCardCard({
  artifact,
  cardSx,
  onAddToDashboard,
  embedTitle = false,
  showCardActions = true,
}: {
  artifact: MetricArtifact;
  cardSx: Record<string, any>;
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
  embedTitle?: boolean;
  /** When false, the canvas header drives pin — hide it on the card. */
  showCardActions?: boolean;
}) {
  const formattedValue = React.useMemo(() => {
    const v = artifact.metricValue;
    if (v === null || v === undefined) return '—';
    if (typeof v === 'number') {
      if (Math.abs(v) >= 1_000_000_000) return (v / 1_000_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 }) + 'B';
      if (Math.abs(v) >= 1_000_000) return (v / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 }) + 'M';
      if (Math.abs(v) >= 1_000) return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
      return v.toLocaleString(undefined, { maximumFractionDigits: 4 });
    }
    return String(v);
  }, [artifact.metricValue]);

  return (
    <Box sx={cardSx}>
      {embedTitle && <ArtifactInlineTitleBlock artifact={artifact} />}
      {/* Card header — only when canvas header isn't carrying the pin action */}
      {showCardActions && (
        <Box
          sx={(t) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 0.875,
            px: 1.75,
            py: 1,
            borderBottom: `1px solid ${t.tokens.border.subtle}`,
          })}
        >
          <SpeedRounded sx={(t) => ({ fontSize: 16, color: t.tokens.text.tertiary })} />
          <Typography sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary })}>
            Metric
          </Typography>
          <Box sx={{ flex: 1 }} />

          {onAddToDashboard && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<DashboardCustomizeRounded sx={{ fontSize: 14 }} />}
              onClick={() => onAddToDashboard(artifact)}
              sx={(t) => ({
                py: 0.375,
                px: 1,
                height: 28,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: `${t.ds.radius.sm}px`,
                lineHeight: 1.5,
                minWidth: 0,
                whiteSpace: 'nowrap',
                borderColor: t.tokens.border.default,
                color: t.tokens.text.secondary,
                '&:hover': {
                  borderColor: t.tokens.brand.primary,
                  color: t.tokens.brand.primary,
                  bgcolor: t.tokens.brand.primarySoft,
                },
              })}
            >
              Pin to Dashboard
            </Button>
          )}
        </Box>
      )}

      {/* Metric display */}
      <Box
        sx={(t) => ({
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          bgcolor: t.tokens.surface.sunken,
          minHeight: 200,
        })}
      >
        <Typography
          sx={(t) => ({
            ...t.typography.eyebrow,
            color: t.tokens.text.tertiary,
            textAlign: 'center',
          })}
        >
          {artifact.metricName}
        </Typography>
        <Typography
          sx={(t) => ({
            fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: t.tokens.text.primary,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.05,
            textAlign: 'center',
          })}
        >
          {formattedValue}
        </Typography>
        {artifact.title && artifact.title !== artifact.metricName && (
          <Typography
            sx={(t) => ({
              fontSize: '0.8125rem',
              color: t.tokens.text.tertiary,
              textAlign: 'center',
              mt: 0.5,
            })}
          >
            {artifact.title}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ============================================================================
// Query Approval Card - inline workspace review for SQL / file / API requests
// ============================================================================

export type SQLApprovalCardVariant = 'full' | 'inline';

export function SQLApprovalCard({
  approval,
  onApprove,
  onEdit,
  onReject,
  variant = 'full',
}: {
  approval: SQLApproval;
  onApprove: () => void;
  onEdit: (editedSql: string) => void;
  onReject: (reason: string) => void;
  variant?: SQLApprovalCardVariant;
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [isEditing, setIsEditing] = useState(false);
  const [editedSql, setEditedSql] = useState(approval.sql);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [copied, setCopied] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');

  const isFile = approval.isFileConnector || approval.connectorCategory === 'file';
  const isApi = approval.isApiConnector || approval.connectorCategory === 'api';
  const isNonSql = isFile || isApi;
  const inline = variant === 'inline';

  // Friendly-label substitution for the approval view (display-only; the
  // SQL the user approves is `approval.sql` — never the substituted form).
  const tenantId = readTenantId();
  const { labels } = useWarehouseLabels(isNonSql ? null : tenantId);
  const displaySql = useMemo(
    () => (isNonSql ? approval.sql : substituteSqlLabels(approval.sql, labels)),
    [approval.sql, labels, isNonSql],
  );

  let formattedSQL = displaySql;
  if (!isNonSql) {
    try { formattedSQL = formatSQL(displaySql, { language: 'postgresql' }); } catch { /* noop */ }
  }

  const handleCopy = () => {
    // Per design: copy what's visible (raw FQNs are never shown to users).
    navigator.clipboard.writeText(isNonSql ? approval.sql : formattedSQL);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const cardSx = {
    border: `1px solid ${theme.tokens.border.subtle}`,
    borderRadius: `${theme.ds.radius.lg}px`,
    bgcolor: theme.tokens.surface.raised,
    boxShadow: theme.tokens.shadow.xs,
    overflow: 'hidden' as const,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: inline ? 'auto' : '100%',
        maxHeight: inline ? 440 : undefined,
        minWidth: 0,
      }}
    >
      {/* Title bar */}
      <Box
        sx={(t) => ({
          height: 48,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          borderBottom: `1px solid ${t.tokens.border.subtle}`,
          bgcolor: t.tokens.semantic.warningSoft,
        })}
      >
        <GppGoodRounded sx={(t) => ({ fontSize: 18, color: t.tokens.semantic.warning })} />
        <Typography
          sx={(t) => ({
            fontWeight: 600,
            fontSize: '0.9375rem',
            letterSpacing: '-0.005em',
            color: t.tokens.text.primary,
            flex: 1,
          })}
        >
          {isFile ? 'Data access approval' : isApi ? 'API request approval' : 'SQL approval required'}
        </Typography>
        <MetaBadge icon={<StorageRounded sx={{ fontSize: 12 }} />} label={approval.connectorName} />
      </Box>

      {/* Scrollable content */}
      <Box
        sx={(t) => ({
          flex: inline ? '0 1 auto' : 1,
          maxHeight: inline ? 360 : undefined,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.75,
          bgcolor: t.tokens.surface.canvas,
        })}
      >
        {/* Description */}
        <Typography
          sx={(t) => ({
            fontSize: '0.875rem',
            color: t.tokens.text.secondary,
            lineHeight: 1.55,
          })}
        >
          {approval.title}
        </Typography>

        {isNonSql ? (
          /* ── File / API connectors: structured info ── */
          <Box sx={cardSx}>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
              <Box>
                <Typography sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary })}>
                  {isFile ? 'Data source' : 'API resource'}
                </Typography>
                <Typography
                  sx={(t) => ({
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    letterSpacing: '-0.005em',
                    color: t.tokens.text.primary,
                    mt: 0.375,
                  })}
                >
                  {isFile
                    ? (approval.entityName || approval.entityId || 'Unknown file')
                    : (approval.resource || 'Unknown resource')}
                </Typography>
                <Typography sx={(t) => ({ fontSize: '0.8125rem', color: t.tokens.text.secondary, mt: 0.25 })}>
                  from {approval.connectorName}
                </Typography>
              </Box>

              {isFile && approval.columns && approval.columns.length > 0 && (
                <Box>
                  <Typography sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary, display: 'block', mb: 0.625 })}>
                    Columns to fetch
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {approval.columns.map((col: string, i: number) => (
                      <Typography
                        key={i}
                        component="span"
                        sx={(t) => ({
                          px: 0.875,
                          py: 0.25,
                          borderRadius: `${t.ds.radius.xs}px`,
                          bgcolor: t.tokens.surface.sunken,
                          fontFamily: t.typography.fontFamilyMono,
                          fontSize: '0.75rem',
                          color: t.tokens.text.secondary,
                        })}
                      >
                        {col}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {isApi && approval.properties && approval.properties.length > 0 && (
                <Box>
                  <Typography sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary, display: 'block', mb: 0.625 })}>
                    Properties
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {approval.properties.map((prop: string, i: number) => (
                      <Typography
                        key={i}
                        component="span"
                        sx={(t) => ({
                          px: 0.875,
                          py: 0.25,
                          borderRadius: `${t.ds.radius.xs}px`,
                          bgcolor: t.tokens.surface.sunken,
                          fontFamily: t.typography.fontFamilyMono,
                          fontSize: '0.75rem',
                          color: t.tokens.text.secondary,
                        })}
                      >
                        {prop}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {isApi && approval.limit != null && (
                <Box>
                  <Typography sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary })}>
                    Limit
                  </Typography>
                  <Typography sx={(t) => ({ fontSize: '0.875rem', color: t.tokens.text.primary, mt: 0.375 })}>
                    {approval.limit}
                  </Typography>
                </Box>
              )}

              <Box>
                <Typography sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary, display: 'block', mb: 0.625 })}>
                  {isFile ? 'Request spec' : 'API request'}
                </Typography>
                <Box
                  sx={(t) => ({
                    p: 1.25,
                    borderRadius: `${t.ds.radius.sm}px`,
                    bgcolor: t.tokens.surface.sunken,
                    border: `1px solid ${t.tokens.border.subtle}`,
                    fontFamily: t.typography.fontFamilyMono,
                    fontSize: '0.8125rem',
                    lineHeight: 1.65,
                    color: t.tokens.text.primary,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  })}
                >
                  {approval.sql}
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          /* ── SQL connector: code card ── */
          <Box sx={cardSx}>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.875,
                px: 1.75,
                py: 1.125,
              }}
            >
              <CodeRounded sx={(t) => ({ fontSize: 16, color: t.tokens.text.tertiary, flexShrink: 0 })} />
              <Typography sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary, flex: 1 })}>
                Proposed SQL
              </Typography>
              <Tooltip title={copied ? 'Copied!' : 'Copy SQL'} placement="top">
                <IconButton
                  size="small"
                  onClick={handleCopy}
                  sx={(t) => ({
                    width: 28, height: 28,
                    color: copied ? t.tokens.semantic.success : t.tokens.text.tertiary,
                    '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.surface.sunken },
                  })}
                >
                  {copied ? <CheckRounded sx={{ fontSize: 14 }} /> : <ContentCopyRounded sx={{ fontSize: 14 }} />}
                </IconButton>
              </Tooltip>
            </Box>

            {/* SQL body */}
            <Box
              sx={(t) => ({
                px: 1,
                pb: 1,
                maxHeight: 420,
                overflow: 'auto',
                borderTop: `1px solid ${t.tokens.border.subtle}`,
              })}
            >
              {isEditing ? (
                <TextField
                  multiline
                  fullWidth
                  minRows={8}
                  maxRows={20}
                  value={editedSql}
                  onChange={(e) => setEditedSql(e.target.value)}
                  sx={(t) => ({
                    mt: 1,
                    '& .MuiInputBase-input': {
                      fontFamily: t.typography.fontFamilyMono,
                      fontSize: '0.8125rem',
                      lineHeight: 1.65,
                    },
                  })}
                />
              ) : (
                <SyntaxHighlighter
                  language="sql"
                  style={isDark ? atomOneDark : atomOneLight}
                  showLineNumbers
                  lineNumberStyle={{
                    minWidth: '2.4em',
                    paddingRight: '1em',
                    color: theme.tokens.text.tertiary,
                    fontSize: '0.75rem',
                    userSelect: 'none',
                  }}
                  customStyle={{
                    margin: 0,
                    padding: '12px',
                    borderRadius: theme.ds.radius.sm,
                    fontSize: '0.8125rem',
                    lineHeight: 1.65,
                    background: theme.tokens.surface.sunken,
                    fontFamily: theme.typography.fontFamilyMono as string,
                  }}
                >
                  {formattedSQL}
                </SyntaxHighlighter>
              )}
            </Box>
          </Box>
        )}

        {/* Reject reason */}
        {showReject && (
          <TextField
            fullWidth
            label="Reason for rejection (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            size="small"
            sx={{ mt: 0.5 }}
          />
        )}

        {/* File feedback */}
        {showFeedback && (
          <TextField
            fullWidth
            label={isFile ? 'What should change? e.g. wrong file, need different columns…' : 'What should change? e.g. wrong resource, need different properties…'}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            size="small"
            multiline
            minRows={2}
            maxRows={4}
            sx={{ mt: 0.5 }}
            autoFocus
          />
        )}

        {/* Action buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          {showReject ? (
            <>
              <Button onClick={() => setShowReject(false)} size="small">
                Back
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelOutlined />}
                onClick={() => onReject(rejectReason)}
                size="small"
              >
                Confirm Reject
              </Button>
            </>
          ) : (
            <>
              <Button
                color="error"
                startIcon={<CancelOutlined />}
                onClick={() => setShowReject(true)}
                size="small"
              >
                Reject
              </Button>
              <Box sx={{ flex: 1 }} />
              {isEditing ? (
                <>
                  <Button
                    onClick={() => { setIsEditing(false); setEditedSql(approval.sql); }}
                    size="small"
                  >
                    Cancel Edit
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleOutlineRounded />}
                    onClick={() => onEdit(editedSql)}
                    disabled={!editedSql.trim()}
                    size="small"
                  >
                    Approve Edited SQL
                  </Button>
                </>
              ) : (
                <>
                  {isNonSql ? (
                    showFeedback ? (
                      <>
                        <Button
                          onClick={() => { setShowFeedback(false); setFeedback(''); }}
                          size="small"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<EditRounded />}
                          onClick={() => onReject(feedback)}
                          disabled={!feedback.trim()}
                          size="small"
                        >
                          Send Feedback
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          startIcon={<EditRounded />}
                          onClick={() => setShowFeedback(true)}
                          size="small"
                        >
                          Suggest Changes
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<CheckCircleOutlineRounded />}
                          onClick={onApprove}
                          size="small"
                        >
                          Approve & Run
                        </Button>
                      </>
                    )
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<EditRounded />}
                        onClick={() => setIsEditing(true)}
                        size="small"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<CheckCircleOutlineRounded />}
                        onClick={onApprove}
                        size="small"
                      >
                        Approve & Run
                      </Button>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ============================================================================
// Meta Badge - tiny label for title bar
// ============================================================================

function MetaBadge({
  icon,
  label,
  muted,
  color,
}: {
  icon?: React.ReactNode;
  label: string;
  muted?: boolean;
  color?: 'warning';
}) {
  const isWarning = color === 'warning';

  return (
    <Box
      sx={(t) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.875,
        py: 0.375,
        borderRadius: `${t.ds.radius.full}px`,
        bgcolor: isWarning ? t.tokens.semantic.warningSoft : t.tokens.surface.sunken,
        color: isWarning
          ? t.tokens.semantic.warning
          : muted
            ? t.tokens.text.tertiary
            : t.tokens.text.secondary,
      })}
    >
      {icon}
      <Typography
        sx={{
          fontSize: '0.75rem',
          fontWeight: 500,
          color: 'inherit',
          lineHeight: 1,
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// ============================================================================
// Empty State
// ============================================================================

// (Inline calculation-trail panel removed — full trail still available via the
// "View sources and calculation trail" link in chat.)

// ── Workspace loading skeleton — shown while chat history is being fetched ───
function WorkspaceLoadingSkeleton() {
  return (
    <Box
      sx={(t) => ({
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: t.tokens.surface.canvas,
        overflow: 'hidden',
      })}
    >
      {/* Title bar skeleton */}
      <Box
        sx={(t) => ({
          px: 3,
          py: 1.75,
          borderBottom: `1px solid ${t.tokens.border.subtle}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
        })}
      >
        <Skeleton variant="rounded" width={28} height={28} sx={{ borderRadius: 1.5, flexShrink: 0 }} animation="wave" />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Skeleton variant="rounded" width="34%" height={14} sx={{ borderRadius: 0.75 }} animation="wave" />
          <Skeleton variant="rounded" width="20%" height={11} sx={{ borderRadius: 0.75 }} animation="wave" />
        </Box>
        <Skeleton variant="rounded" width={72} height={30} sx={{ borderRadius: 1.5, flexShrink: 0 }} animation="wave" />
      </Box>

      {/* Card body skeleton */}
      <Box
        sx={(t) => ({
          flex: 1,
          px: 3,
          py: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflow: 'hidden',
        })}
      >
        {/* SQL card */}
        <Box
          sx={(t) => ({
            borderRadius: `${t.ds.radius.md}px`,
            border: `1px solid ${t.tokens.border.subtle}`,
            overflow: 'hidden',
          })}
        >
          <Box sx={(t) => ({ px: 2, py: 1.25, borderBottom: `1px solid ${t.tokens.border.subtle}`, display: 'flex', alignItems: 'center', gap: 1 })}>
            <Skeleton variant="rounded" width={14} height={14} sx={{ borderRadius: 0.5, flexShrink: 0 }} animation="wave" />
            <Skeleton variant="rounded" width="18%" height={12} sx={{ borderRadius: 0.5 }} animation="wave" />
          </Box>
          <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {['55%', '70%', '45%'].map((w, i) => (
              <Skeleton key={i} variant="rounded" width={w} height={12} sx={{ borderRadius: 0.5 }} animation="wave" />
            ))}
          </Box>
        </Box>

        {/* Table card */}
        <Box
          sx={(t) => ({
            borderRadius: `${t.ds.radius.md}px`,
            border: `1px solid ${t.tokens.border.subtle}`,
            overflow: 'hidden',
            flex: 1,
          })}
        >
          {/* Table header row */}
          <Box
            sx={(t) => ({
              px: 2,
              py: 1,
              borderBottom: `1px solid ${t.tokens.border.subtle}`,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              bgcolor: t.tokens.surface.raised,
            })}
          >
            {['14%', '18%', '12%', '16%'].map((w, i) => (
              <Skeleton key={i} variant="rounded" width={w} height={11} sx={{ borderRadius: 0.5 }} animation="wave" />
            ))}
          </Box>
          {/* Table data rows */}
          {[0, 1, 2, 3, 4].map((row) => (
            <Box
              key={row}
              sx={(t) => ({
                px: 2,
                py: 0.875,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                borderBottom: `1px solid ${t.tokens.border.subtle}`,
                '&:last-child': { borderBottom: 'none' },
              })}
            >
              {(['12%', '20%', '10%', '14%'] as const).map((w, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  width={w}
                  height={10}
                  sx={{ borderRadius: 0.5, opacity: 1 - row * 0.12 }}
                  animation="wave"
                />
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export function WorkspaceEmptyState({
  isStreaming,
  streamingAction,
}: {
  isStreaming?: boolean;
  streamingAction?: string;
}) {
  const isLoadingHistory = useCopilotStore((s: any) => s.isLoadingHistory) as boolean;

  if (isStreaming) {
    return <WorkspaceWorkingState action={streamingAction} />;
  }
  if (isLoadingHistory) {
    return <WorkspaceLoadingSkeleton />;
  }
  return (
    <Box
      sx={(t) => ({
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2,
        height: '100%',
        bgcolor: t.tokens.surface.canvas,
        px: 3,
      })}
    >
      <Box
        sx={(t) => ({
          width: 56,
          height: 56,
          borderRadius: `${t.ds.radius.lg}px`,
          bgcolor: t.tokens.brand.primarySoft,
          border: `1px solid ${t.tokens.border.subtle}`,
          color: t.tokens.brand.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        })}
      >
        <SmartToyOutlined sx={{ fontSize: 26 }} />
      </Box>
      <Box sx={{ textAlign: 'center', maxWidth: 360 }}>
        <Typography
          sx={(t) => ({
            fontWeight: 600,
            mb: 0.5,
            fontSize: '0.9375rem',
            color: t.tokens.text.primary,
            letterSpacing: '-0.005em',
          })}
        >
          Your results land here
        </Typography>
        <Typography
          sx={(t) => ({
            color: t.tokens.text.secondary,
            lineHeight: 1.6,
            fontSize: '0.8125rem',
          })}
        >
          Tables, charts and metrics generated by Copilot show up here. Ask a question on the right to begin.
        </Typography>
      </Box>
      <Box
        sx={(t) => ({
          mt: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: t.tokens.text.tertiary,
          fontSize: '0.6875rem',
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        })}
      >
        <KbdHint label="?" /> shortcuts
      </Box>
    </Box>
  );
}

function KbdHint({ label }: { label: string }) {
  return (
    <Box
      component="span"
      sx={(t) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 18,
        height: 18,
        px: 0.625,
        borderRadius: 0.75,
        bgcolor: t.tokens.surface.raised,
        border: `1px solid ${t.tokens.border.subtle}`,
        color: t.tokens.text.secondary,
        fontFamily: t.typography.fontFamilyMono,
        fontSize: '0.625rem',
        fontWeight: 600,
        letterSpacing: 0,
        textTransform: 'none',
      })}
    >
      {label}
    </Box>
  );
}

const COPILOT_TIPS = [
  {
    label: 'Tip',
    text: 'Ask follow-up questions — Copilot remembers the full context of your conversation, so you can refine results without starting over.',
  },
  {
    label: 'Tip',
    text: 'Pin any chart or table to your dashboard so you can track key metrics at a glance without re-running the analysis.',
  },
  {
    label: 'Tip',
    text: 'Ask Copilot to segment results — e.g. "break this down by region and plan tier" — to instantly spot differences across your customer base.',
  },
  {
    label: 'Tip',
    text: 'Every table has a one-click CSV export. Great for sharing results with teammates who work outside Klaris.',
  },
  {
    label: 'Tip',
    text: 'Predictive models learn from your historical data. The more enriched your data, the sharper the churn or lapse predictions.',
  },
  {
    label: 'Tip',
    text: "Ask \"why?\" or \"explain this\" after any result and Copilot will give you a plain-language breakdown of what's driving the numbers.",
  },
  {
    label: 'Tip',
    text: 'You can ask Copilot to write and explain the SQL behind any result — great for auditing the logic or copying it into your own pipelines.',
  },
];

function RotatingTip() {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * COPILOT_TIPS.length));
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % COPILOT_TIPS.length);
        setVisible(true);
      }, 350);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const tip = COPILOT_TIPS[index];

  return (
    <Box
      sx={{
        transition: 'opacity 0.35s ease, transform 0.35s ease',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(4px)',
      }}
    >
      <Typography
        component="span"
        sx={(t) => ({
          display: 'inline-block',
          fontSize: '0.6875rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: t.tokens.brand.primary,
          mb: 0.5,
        })}
      >
        {tip.label}
      </Typography>
      <Typography
        sx={(t) => ({
          fontSize: '0.8125rem',
          color: t.tokens.text.secondary,
          lineHeight: 1.6,
        })}
      >
        {tip.text}
      </Typography>
    </Box>
  );
}

function WorkspaceWorkingState({ action }: { action?: string }) {
  const label = action && action.trim().length > 0 ? action : 'Working on it...';
  return (
    <Box
      sx={(t) => ({
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2.5,
        height: '100%',
        bgcolor: t.tokens.surface.canvas,
        px: 3,
        textAlign: 'center',
      })}
    >
      <Box
        sx={(t) => ({
          position: 'relative',
          width: 56,
          height: 56,
          borderRadius: `${t.ds.radius.lg}px`,
          bgcolor: t.tokens.brand.primarySoft,
          border: `1px solid ${t.tokens.border.subtle}`,
          color: t.tokens.brand.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '@keyframes wsPulseRing': {
            '0%': { transform: 'scale(1)', opacity: 0.8 },
            '70%': { transform: 'scale(1.35)', opacity: 0 },
            '100%': { transform: 'scale(1.35)', opacity: 0 },
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            border: `1px solid ${t.tokens.brand.primary}`,
            animation: 'wsPulseRing 1.6s ease-out infinite',
          },
        })}
      >
        <SmartToyOutlined sx={{ fontSize: 26 }} />
      </Box>
      <Box sx={{ maxWidth: 360 }}>
        <Typography
          sx={(t) => ({
            fontSize: '0.9375rem',
            fontWeight: 600,
            color: t.tokens.text.primary,
            letterSpacing: '-0.005em',
            mb: 0.5,
          })}
        >
          {label}
        </Typography>
        <RotatingTip />
      </Box>
      {/* Skeleton placeholder */}
      <Box
        sx={(t) => ({
          width: '100%',
          maxWidth: 520,
          mt: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          '@keyframes wsSkeletonShimmer': {
            '0%': { backgroundPosition: '-200% 0' },
            '100%': { backgroundPosition: '200% 0' },
          },
        })}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={(t) => ({
              height: i === 0 ? 26 : 14,
              borderRadius: 1,
              opacity: i === 0 ? 1 : 0.7,
              background: `linear-gradient(90deg, ${t.tokens.surface.sunken} 0%, ${t.tokens.border.subtle} 50%, ${t.tokens.surface.sunken} 100%)`,
              backgroundSize: '200% 100%',
              animation: 'wsSkeletonShimmer 1.6s linear infinite',
            })}
          />
        ))}
      </Box>
    </Box>
  );
}
